import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { Camera, X, SwitchCamera, AlertTriangle } from 'lucide-react';

interface QrCodeScannerProps {
  isOpen: boolean;
  onClose: () => void;
  onScan: (decodedText: string) => void;
}

const SCANNER_ID = 'qr-scanner-region';

export function QrCodeScanner({ isOpen, onClose, onScan }: QrCodeScannerProps) {
  const [error, setError] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const hasScannedRef = useRef(false);
  const isMountedRef = useRef(true);
  // Store callbacks in refs to avoid stale closures inside scanner callback
  const onScanRef = useRef(onScan);
  const onCloseRef = useRef(onClose);
  onScanRef.current = onScan;
  onCloseRef.current = onClose;

  const cleanup = useCallback(async () => {
    const scanner = scannerRef.current;
    if (!scanner) return;
    try {
      const state = scanner.getState();
      if (state === Html5QrcodeScannerState.SCANNING) {
        await scanner.stop();
      }
    } catch { /* ignore */ }
    try { scanner.clear(); } catch { /* ignore */ }
    scannerRef.current = null;
  }, []);

  const startCamera = useCallback(async (facing: 'environment' | 'user') => {
    setIsStarting(true);
    setError(null);
    hasScannedRef.current = false;

    await cleanup();

    // Wait for DOM to have the container
    await new Promise(r => setTimeout(r, 200));
    if (!isMountedRef.current) return;

    const el = document.getElementById(SCANNER_ID);
    if (!el) {
      setIsStarting(false);
      setError('Container do scanner não encontrado.');
      return;
    }

    try {
      const scanner = new Html5Qrcode(SCANNER_ID);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: facing },
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1 },
        (decodedText) => {
          if (!hasScannedRef.current) {
            hasScannedRef.current = true;
            // Use refs to avoid stale closures
            onScanRef.current(decodedText);
            // Stop scanner then close
            cleanup().then(() => {
              if (isMountedRef.current) onCloseRef.current();
            });
          }
        },
        () => { /* frame without QR — ignore */ },
      );
    } catch (err: any) {
      if (!isMountedRef.current) return;
      const msg = err?.message || String(err);
      if (msg.includes('NotAllowedError') || msg.includes('Permission')) {
        setError('Permissão de câmera negada. Habilite nas configurações do navegador.');
      } else if (msg.includes('NotFoundError') || msg.includes('no camera') || msg.includes('Requested device not found')) {
        setError('Nenhuma câmera encontrada neste dispositivo.');
      } else if (msg.includes('NotReadableError') || msg.includes('Could not start')) {
        setError('Câmera em uso por outro aplicativo. Feche outros apps e tente novamente.');
      } else {
        setError(`Erro ao acessar câmera: ${msg}`);
      }
    } finally {
      if (isMountedRef.current) setIsStarting(false);
    }
  }, [cleanup]);

  // Start/stop based on isOpen
  useEffect(() => {
    isMountedRef.current = true;
    if (isOpen) {
      startCamera(facingMode);
    } else {
      cleanup();
    }
    return () => {
      isMountedRef.current = false;
      cleanup();
    };
  }, [isOpen]); // Only react to open/close — NOT facingMode (handled by switchCamera)

  const handleSwitchCamera = async () => {
    const newFacing = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newFacing);
    await startCamera(newFacing); // Pass directly — don't rely on state
  };

  const handleClose = () => {
    cleanup();
    onClose();
  };

  const handleRetry = () => {
    setError(null);
    startCamera(facingMode);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/50">
        <h2 className="text-white font-semibold text-lg">Escanear QR Code PIX</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSwitchCamera}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            title="Trocar câmera"
          >
            <SwitchCamera className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={handleClose}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>

      {/* Scanner area */}
      <div className="flex-1 flex flex-col items-center justify-center px-4">
        {error ? (
          <div className="text-center space-y-4 max-w-sm">
            <AlertTriangle className="w-16 h-16 text-yellow-400 mx-auto" />
            <p className="text-white text-sm">{error}</p>
            <button
              onClick={handleRetry}
              className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              Tentar novamente
            </button>
          </div>
        ) : (
          <>
            {isStarting && (
              <div className="absolute inset-0 flex items-center justify-center z-10 pointer-events-none">
                <div className="text-center space-y-3">
                  <Camera className="w-12 h-12 text-white/60 mx-auto animate-pulse" />
                  <p className="text-white/60 text-sm">Iniciando câmera...</p>
                </div>
              </div>
            )}
            <div
              id={SCANNER_ID}
              className="w-full max-w-sm rounded-xl overflow-hidden"
              style={{ minHeight: 300 }}
            />
          </>
        )}
      </div>

      {/* Footer */}
      <div className="p-4 text-center">
        <p className="text-white/50 text-xs">
          Aponte a câmera para o QR Code PIX
        </p>
      </div>
    </div>
  );
}
